import Decimal from 'decimal.js';

/**
 * One extra offset line a bank rule carves out of each matched amount.
 *
 * `percent` takes that share of the transaction; `amount` takes a fixed sum
 * whatever the transaction is. The rule's target account takes whatever is
 * left, so the lines always net to the amount that moved through the bank
 * and a rule keeps working when the bill is a few dollars different. Splits
 * may also account for the whole amount (1,250 + 1,250 of a 2,500 transfer);
 * the target then gets no line of its own.
 */
export interface BankRuleSplit {
  accountId: string;
  kind: 'percent' | 'amount';
  /** Percent (0 < v <= 100) or a positive money value, as a string. */
  value: string;
  memo?: string | null;
}

/** A line for the match service: account + the portion of the amount it takes. */
export interface RuleSplitLine {
  accountId: string;
  amount: string;
  memo?: string;
}

/**
 * Resolve a rule's splits against one transaction amount.
 *
 * Returns the full set of offset lines — the target account's remainder, when
 * there is one, followed by the configured splits — with every amount rounded
 * to cents, or null when the fixed pieces exceed the amount (a rule meant for
 * a 1,500 payment applied to a 900 one). A remainder of exactly zero is fine:
 * the splits are the whole posting and the target gets no line. Rounding
 * never creates a gap: the remainder is what is left after rounding each
 * split, so the lines always sum to the transaction exactly.
 *
 * Pure and shared so the sync job and the match drawer compute the same lines.
 */
export function computeRuleSplits(
  rule: { targetAccountId: string; splits?: BankRuleSplit[] | null },
  txAmount: string,
): RuleSplitLine[] | null {
  const total = new Decimal(txAmount).abs().toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  const splits = rule.splits ?? [];
  if (splits.length === 0) return [{ accountId: rule.targetAccountId, amount: total.toFixed(2) }];

  const lines: RuleSplitLine[] = [];
  let taken = new Decimal(0);
  for (const s of splits) {
    const value = new Decimal(s.value);
    const piece =
      s.kind === 'percent'
        ? total.mul(value).div(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
        : value.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    if (piece.lte(0)) continue;
    taken = taken.plus(piece);
    lines.push({
      accountId: s.accountId,
      amount: piece.toFixed(2),
      ...(s.memo ? { memo: s.memo } : {}),
    });
  }
  const remainder = total.minus(taken);
  if (remainder.lt(0)) return null;
  if (remainder.isZero()) return lines.length > 0 ? lines : null;
  return [{ accountId: rule.targetAccountId, amount: remainder.toFixed(2) }, ...lines];
}
